import { ReactNode } from "react";
import URLCalendar from "./URLCalendar";
import InstructionStepper, {
  instructionPage,
  noImageInstructionPage,
} from "./InstructionStepper";
import GoogleCalendarAdvisorOptions from "./GoogleCalendarAdvisorOptions";
import GoogleCalendarMemberOptions from "./GoogleCalendarMemberOptions";
import AppleCalendar from "./AppleCalendar";
import { Breakpoint } from "@mui/material";
import FileUploadCalendar from "./FileUploadCalendar";

export abstract class ImportCalendar {
  title: string;
  modalTitle: string;
  name: string;
  steps?: Array<string | ReactNode>;

  constructor(
    name: string,
    title: string,
    steps?: Array<string | ReactNode>,
    modalTitle?: string,
  ) {
    this.name = name;
    this.title = title;
    this.modalTitle = modalTitle || title;
    this.steps = steps;
  }

  getModalWidth(): Breakpoint | null {
    return null;
  }

  getModalTitle(): string {
    return this.modalTitle;
  }

  abstract getContent(
    onSuccess: (url: string) => void,
    onFailure: () => void,
    familyRef?: string,
  ): ReactNode;
}

class GoogleImportCalendar extends ImportCalendar {
  memberOptions: boolean;

  constructor(memberOptions: boolean = false) {
    super("google", "Google Calendar");
    this.memberOptions = memberOptions;
  }

  getContent() {
    if (this.memberOptions) {
      return <GoogleCalendarMemberOptions />;
    }
    return <GoogleCalendarAdvisorOptions />;
  }

  getModalWidth(): Breakpoint | null {
    return "sm";
  }
}

class AppleImportCalendar extends ImportCalendar {
  constructor() {
    super("apple", "Apple Calendar");
  }

  getContent(
    onSuccess: (url: string) => void,
    onFailure: () => void,
    familyRef?: string,
  ) {
    return (
      <AppleCalendar
        familyRef={familyRef}
        instructions={
          <InstructionStepper
            steps={[
              instructionPage(
                "/assets/images/calendars/imports/examples/apple/apple-import-step1.png",
                "horizontal",
                [
                  {
                    number: 1,
                    content: (
                      <>
                        In Apple Calendar,{" "}
                        <b>click the Calendar icon button </b>
                        in the top-left corner to view the list of your
                        calendars.
                      </>
                    ),
                  },
                  {
                    number: 2,
                    content: (
                      <>
                        Locate the calendar you want to share, then right-click
                        on it.
                      </>
                    ),
                  },
                  {
                    number: 3,
                    content: (
                      <>
                        Select <b>Sharing Settings</b>.
                      </>
                    ),
                  },
                ],
              ),
              instructionPage(
                "/assets/images/calendars/imports/examples/apple/apple-import-step2.png",
                "horizontal",
                [
                  {
                    number: 4,
                    content: (
                      <>
                        <b>Check the box for "Public Calendar"</b> (if
                        available). This will only make the calendar public to
                        those with the secret link generated.
                      </>
                    ),
                  },
                ],
              ),
              instructionPage(
                "/assets/images/calendars/imports/examples/apple/apple-import-step3.png",
                "horizontal",
                [
                  {
                    number: 5,
                    content: (
                      <>
                        Copy the calendar URL (beginning with <b>webcal://</b>)
                        and paste it below.
                      </>
                    ),
                  },
                ],
              ),
            ]}
          />
        }
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
    );
  }
}

class URLImportCalendar extends ImportCalendar {
  steps: ReactNode[];
  constructor(name: string, title: string, steps: ReactNode[]) {
    // super("url", "URL Calendar");
    super(name, title);
    this.steps = steps;
  }

  getContent(
    onSuccess: (url: string) => void,
    onFailure: () => void,
    familyRef?: string,
  ) {
    return (
      <URLCalendar
        familyRef={familyRef}
        instructions={<InstructionStepper steps={this.steps} />}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
    );
  }
}

class FileUploadImportCalendar extends ImportCalendar {
  constructor() {
    super("file-upload", "Upload File");
  }

  getContent(
    onSuccess: (url: string) => void,
    onFailure: () => void,
    familyRef?: string,
  ) {
    return (
      <FileUploadCalendar
        familyRef={familyRef}
        onSuccess={onSuccess}
        onFailure={onFailure}
        instructions={
          <InstructionStepper
            steps={[
              instructionPage(
                "/assets/images/calendars/imports/examples/upload/calendar-upload.jpeg",
                "horizontal",
                [
                  {
                    content: (
                      <>
                        Upload an ICS calendar file (.ics) from your device to
                        faye. Many public calendars will let you download a
                        calendar and this typically comes as an "ICS" file.
                      </>
                    ),
                  },
                  {
                    content: (
                      <>
                        Additionally calendar applications - like Apple, Google
                        , Outlook, and most others - can export calendars in ICS
                        format. Look for "Export" or "Export to ICS" options in
                        your calendar settings.
                      </>
                    ),
                  },
                ],
              ),
            ]}
          />
        }
      />
    );
  }
}

export const GoogleImporterWithOptions = new GoogleImportCalendar();
export const GoogleImporterMemberOptions = new GoogleImportCalendar(true);
export const AppleImporter = new AppleImportCalendar();
export const FileUploadImporter = new FileUploadImportCalendar();
export const GenericURLImporter = new URLImportCalendar(
  "Other",
  "Other calendar",
  [
    noImageInstructionPage([
      {
        content: (
          <>
            Most Calendar tools allow for exporting, sharing, or subscriptions
            with other calendar tools by giving you a special URL. If you have a
            URL, paste it below and we can try to import it.
          </>
        ),
      },
      {
        content: (
          <>
            If you still cannot import your desired calendar, reach out for help
            at{" "}
            <a target="_blank" href="mailto:support@findfaye.com">
              support@findfaye.com
            </a>{" "}
            and we can give you a hand.
          </>
        ),
      },
    ]),
  ],
);
export const CoziImporter = new URLImportCalendar("cozi", "Cozi Calendar", [
  instructionPage(
    "/assets/images/calendars/imports/examples/cozi/cozi-import-1.png",
    "horizontal",
    [
      {
        number: 1,
        content: (
          <>
            Log in to your Cozi account via the mobile app or at{" "}
            <a
              href="https://my.cozi.com"
              target="_blank"
              style={{
                color: "#198282",
                textDecoration: "none",
                fontWeight: "700",
              }}
            >
              my.cozi.com
            </a>
            .
          </>
        ),
      },
      {
        number: 2,
        content: (
          <>
            Navigate to <b>Settings</b> in the sidebar.
          </>
        ),
      },
    ],
  ),
  instructionPage(
    "/assets/images/calendars/imports/examples/cozi/cozi-import-2.png",
    "horizontal",
    [
      {
        number: 3,
        content: (
          <>
            Within Settings, select <b>Shared Cozi Calendars</b>.
          </>
        ),
      },
    ],
  ),
  instructionPage(
    "/assets/images/calendars/imports/examples/cozi/cozi-import-3.png",
    "horizontal",
    [
      {
        number: 4,
        content: (
          <>
            Choose the family member's calendar you wish to share and toggle
            switch to enable sharing for that calendar.
          </>
        ),
      },
      {
        number: 5,
        content: (
          <>
            Click <b>VIEW OR SEND COZI URL</b>.
          </>
        ),
      },
    ],
  ),
  instructionPage(
    "/assets/images/calendars/imports/examples/cozi/cozi-import-4.png",
    "horizontal",
    [
      {
        number: 6,
        content: (
          <>
            Copy the provided ICS URL and paste in the input box below these
            instructions.
          </>
        ),
      },
    ],
  ),
]);

export const OutlookImporter = new URLImportCalendar(
  "outlook",
  "Outlook Calendar",
  [
    instructionPage(
      "/assets/images/calendars/imports/examples/outlook/outlook-import-1.png",
      "horizontal",
      [
        {
          number: 1,
          content: (
            <>
              Navigate to Outlook’s{" "}
              <a
                href="https://outlook.live.com/calendar/0/options/calendar/SharedCalendars"
                target="_blank"
                style={{
                  color: "#198282",
                  textDecoration: "none",
                  fontWeight: "700",
                }}
              >
                Shared calendars
              </a>{" "}
              settings by going to{" "}
              <b>Settings → Calendars → Shared calendars</b>
            </>
          ),
        },
      ],
    ),
    instructionPage(
      "/assets/images/calendars/imports/examples/outlook/outlook-import-2.png",
      "vertical",
      [
        {
          number: 2,
          content: (
            <>
              Select your calendar and permissions and click <b>Publish</b>”
            </>
          ),
        },
      ],
    ),
    instructionPage(
      "/assets/images/calendars/imports/examples/outlook/outlook-import-3.png",
      "vertical",
      [
        {
          number: 3,
          content: (
            <>
              Copy the <b>ICS link</b> and paste it below
            </>
          ),
        },
      ],
    ),
  ],
);
export const YahooImporter = new URLImportCalendar("yahoo", "Yahoo Calendar", [
  instructionPage(
    "/assets/images/calendars/imports/examples/yahoo/yahoo-import-1.png",
    "horizontal",
    [
      {
        number: 1,
        content: (
          <>
            Open{" "}
            <a
              style={{
                color: "#198282",
                textDecoration: "none",
                fontWeight: "700",
              }}
              target="_blank"
              href="https://calendar.yahoo.com/"
            >
              Yahoo Calendar
            </a>
            .
          </>
        ),
      },
      {
        number: 2,
        content: (
          <>
            Select the dropdown next to your calendar and select{" "}
            <b>Edit Calendar</b>.
          </>
        ),
      },
    ],
  ),
  instructionPage(
    "/assets/images/calendars/imports/examples/yahoo/yahoo-import-2.png",
    "horizontal",
    [
      {
        number: 3,
        content: (
          <>
            Copy the shareable <b>ICS link</b> and paste it below.
          </>
        ),
      },
    ],
  ),
]);

export const MEMBER_IMPORT_CALENDARS = [
  GoogleImporterMemberOptions,
  AppleImporter,
  GenericURLImporter,
  CoziImporter,
  OutlookImporter,
  YahooImporter,
  FileUploadImporter,
];

export const ADVISOR_IMPORT_CALENDARS = [
  GoogleImporterWithOptions,
  AppleImporter,
  CoziImporter,
  OutlookImporter,
  YahooImporter,
  GenericURLImporter,
  FileUploadImporter,
];

export const ADVISOR_FAMILY_IMPORT_CALENDARS = [
  AppleImporter,
  GenericURLImporter,
  CoziImporter,
  OutlookImporter,
  YahooImporter,
  FileUploadImporter,
];
